import { Fragment, useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '@cdn/i18n';
import { NotFound, Unavailable, Waiting } from '@cdn/ui';
import { useSession } from './context';

/**
 * Binds the views below it to the organization named by the address. An organization the account does not
 * belong to gives the not-found state: typing the address of someone else's resource reveals nothing. One the
 * person belonged to while signed in here and lost says so, and that signing in again will not help (D26).
 *
 * The children are keyed by the organization, so every model and subscription created under the previous
 * organization is destroyed when the scope changes.
 */
export function OrganizationScope({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
	const { t } = useI18n();
	const session = useSession();
	const { organization = '' } = useParams();
	const member = session.member(organization);
	const departed = member ? undefined : session.ending.departed(organization);

	useEffect(() => {
		member && session.select(organization);
	}, [member, organization, session]);

	if (departed) return <Unavailable kind="access" title={t('identity.departed.title', { organization: departed })} reason={t('identity.departed.text', { organization: departed })} action={fallback} />;
	if (!member) return <NotFound action={fallback} />;
	if (session.organization?.organization !== organization) return <Waiting />;
	// The account is part of the key: another account signing in on this tab starts from nothing. A fragment, so
	// the page's parts stay the family page's own blocks
	return <Fragment key={`${session.user?.id}:${organization}`}>{children}</Fragment>;
}
